import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { OptionValue } from 'components/common/CustomizedStatusSelect/CustomizedStatusSelect.types';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import { LineChart } from 'components/common/charts/lineChart/lineChart';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/CriticalAnalysisIntensity/criticalAnalysisIntensity.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { InputOption } from 'components/DesignSystem/Select/components/InputOption/InputOption';
import { ValueContainer } from 'components/DesignSystem/Select/components/ValueContainer/ValueContainer';
import { Switch } from 'components/DesignSystem/Switch';
import { useScurveProvider } from 'components/widgets/SCurve/Scurve.provider';
import { graphLineOptions, mapper } from 'components/widgets/SCurve/Scurve.utils';
import { useScurveLogic } from 'components/widgets/SCurve/useScurve.logic';

export const CriticalityWeightedSCurve = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const {
        componentKey,
        routeId: id,
        endpoints,
        name: configTitle,
    } = useConfigurationStore().criticalityWeightedSCurve;

    // Provider
    const { ui, api } = useScurveProvider({
        componentKey,
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
        endpoints,
        widgetViewMode,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.scurveGraph.payload,
        widgetEndpoint: api.scurveGraph.endpoint,
    });

    // Logic
    const { logic } = useScurveLogic({
        ui,
        api,
        widgetViewMode,
        setIsPayloadReady,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <div className={classes.graphOptionFilter}>
                        <div>Line Options</div>
                        <Select<OptionValue, true>
                            value={ui.vars.graphLineOptions.map(mapper)}
                            options={graphLineOptions.map(mapper)}
                            onChange={(value) => {
                                ui.setters.setGraphLineOptions(
                                    value.map((item) => ({
                                        label: item.label,
                                        name: item.value,
                                    })),
                                );
                            }}
                            id={'Line-options-filter'}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isMulti={true}
                            placeholder={'Select Curve option(s)...'}
                            components={{
                                Option: InputOption,
                                ValueContainer: ValueContainer,
                            }}
                        />
                    </div>
                ),
                !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <div data-testid={'annotation-filter'} className={classes.annotationFilter}>
                        <Switch
                            onChange={(event) => {
                                ui.setters.setShowAnnotations(event.target?.checked);
                            }}
                            checked={ui.state.showAnnotations}
                            label={ui.state.showAnnotations ? 'Hide Annotations' : 'Show Annotations'}
                            labelPlacement={'start'}
                        />
                    </div>
                ),
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={propsTitle ?? configTitle}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    shouldHide={true}
                    shouldUseNewIcon={true}
                />,
                <AddToReport
                    key={'addWidgetToReport'}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    uiState={ui.state}
                    shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                    apiPayload={api.scurveGraph.payload}
                    widgetEndpoint={api.scurveGraph.endpoint}
                />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown key={'export'} title={propsTitle ?? configTitle} subTitle={subTitle} />
                ),
                <TooltipIcon key={'info'} tooltip={api.scurveGraph.instance.data?.info} />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={propsTitle ?? configTitle}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                <PinboardPills
                    key={'pinboardPills'}
                    isInPinboard={ui.isInPinboard}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
            ]}
        >
            <LineChart
                height={logic.chartHeight}
                series={logic.series}
                options={logic.options}
                noData={
                    logic.noDataCondition ? (
                        <NoData>{logic.notApplicable ? 'Not applicable with applied filter' : null}</NoData>
                    ) : undefined
                }
                isFetching={api.scurveGraph.instance.isFetching}
                className={classes.lineChart}
            />
        </WidgetWithTitle>
    );
};
